/**
 * Print Depot — front / back artwork rows
 *
 * Shows the back upload row while a "Printed Sides" option reads double-sided,
 * and hides it again when the shopper switches back.
 *
 * The containers themselves are never created or removed — Upload-Lift mounts
 * into them once and needs them to stay put. Only the hidden attribute moves.
 *
 * Two things this has to get right, both learned the hard way:
 *
 *   1. The variant picker sits in its OWN <form class="variant-picker__form">,
 *      separate from the cart form the upload rows live in. A listener bound to
 *      the cart form never sees the option change, which is why the back row
 *      only appeared after a reload.
 *
 *   2. Horizon morphs the variant picker after each change, replacing those
 *      inputs. Anything bound directly to them dies on the first switch.
 *
 * So the change listener is delegated from the document, which survives both.
 * shopify:product:select is the theme's own event and bubbles to the document
 * too; it is here as a second trigger for the case where the selection is
 * changed by something other than a click on the control.
 */
(() => {
  const SIDES = /side/i;
  const DOUBLE = /double|both|two|2/i;

  /**
   * The current "printed sides" answer within one product section.
   *
   * Read from a Shopify variant option where the product has one, and
   * otherwise from a configurator control of the same name. Shopify caps a
   * product at three options, so a product whose three slots are already spoken
   * for can still offer front and back artwork by carrying printed sides as a
   * configuration control instead — Brochures is the case that needs this.
   */
  const readSides = (scope) => {
    for (const input of scope.querySelectorAll('input[data-option-name]:checked')) {
      if (SIDES.test(input.dataset.optionName || '')) return input.value || '';
    }
    for (const select of scope.querySelectorAll('select[data-option-name]')) {
      if (SIDES.test(select.dataset.optionName || '')) return select.value || '';
    }

    // Configurator controls, which are line item properties rather than options.
    for (const input of scope.querySelectorAll('input[data-pd-config-input]:checked')) {
      if (SIDES.test(input.dataset.pdConfigInput || '')) return input.value || '';
    }
    for (const select of scope.querySelectorAll('select[data-pd-config-input]')) {
      if (SIDES.test(select.dataset.pdConfigInput || '')) return select.value || '';
    }
    // Fallback for a picker that does not carry data-option-name: read the
    // fieldset's own label instead.
    for (const input of scope.querySelectorAll('input[type="radio"]:checked')) {
      const label = input.closest('fieldset')?.querySelector('legend')?.textContent ?? '';
      if (SIDES.test(label)) return input.value || '';
    }
    return null;
  };

  const syncOne = (back) => {
    // The picker and the upload rows are in different forms but the same
    // section, so the section is the scope that reliably contains both.
    const scope = back.closest('.shopify-section') || document;
    const value = readSides(scope);

    // No Printed Sides option on this product: leave the row exactly as the
    // template rendered it rather than guessing at it.
    if (value === null) return;

    if (DOUBLE.test(value)) back.removeAttribute('hidden');
    else back.setAttribute('hidden', '');
  };

  const sync = () => {
    for (const back of document.querySelectorAll('[data-pd-upload-side="back"]')) syncOne(back);
  };

  // Delegated: survives the picker being morphed, and does not care which form
  // the control belongs to.
  document.addEventListener('change', (event) => {
    const el = event.target;
    if (!(el instanceof Element)) return;
    if (el.matches('input[data-option-name], select[data-option-name], input[type="radio"], select')) sync();
  });

  document.addEventListener('shopify:product:select', sync);
  document.addEventListener('shopify:section:load', sync);
  document.addEventListener('DOMContentLoaded', sync);

  sync();
})();
